import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Tooltip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Videocam, PlayArrow, Pause, Replay10, Download, Image } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Skeleton from '@mui/material/Skeleton';
import logo from './../../../assets/images/image-not-found.jpg';
import { AddButton } from '../../../utils/common/styles/commonStyles';
import { resetNearestGPSRecord } from '../store/dashboard.action';
import { useDispatch, useSelector } from 'react-redux';
import {
  eventImageUrl,
  eventImageUrlLoading,
  eventImageUrlError,
} from '../store/dashboard.selectors';

// Styled components
const ImageContainer = styled(Box)({
  position: 'relative',
  '&:hover': {
    '& .MuiBox-root': {
      opacity: 1,
    },
  },
});

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows.heavy,
}));

const VideoControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
  marginTop: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows.video,
}));

const MediaComponent = ({
  selectedMarker,
  driveVideoURL,
  loading,
  setLoading,
  showVideo,
  isAddSign,
  activeStep,
  capturedFrame,
  setCapturedFrame,
}) => {
  const videoRef = useRef(null);
  const imageRef = useRef(null);
  const capturedFrameRef = useRef(null);
  const eventImage = useSelector(eventImageUrl);
  const eventImageLoading = useSelector(eventImageUrlLoading);
  const eventImageError = useSelector(eventImageUrlError);

  const dispatch = useDispatch();

  const [videoUrl, setVideoUrl] = useState('');
  const [localShowVideo, setLocalShowVideo] = useState(!!showVideo);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (Object.keys(selectedMarker).length === 0) setLoading(true);
    else setLoading(false);
  }, [selectedMarker, setLoading]);

  useEffect(() => {
    if (videoUrl === '') {
      setVideoUrl(driveVideoURL);
    }
  }, []);

  useEffect(() => {
    setLocalShowVideo(!!showVideo);
  }, [showVideo]);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (
      localShowVideo &&
      videoRef.current &&
      selectedMarker.timestamp &&
      typeof selectedMarker.timestamp === 'number'
    ) {
      videoRef.current.currentTime = selectedMarker.timestamp / 1000;
    }
  }, [localShowVideo, selectedMarker.timestamp, videoUrl]);

  const captureFrame = () => {
    if (!showVideo) return;

    const video = videoRef.current || document.querySelector('video');
    if (!video) return;

    if (!video.paused) return;
    dispatch(resetNearestGPSRecord());
    const msTime = video.currentTime * 1000;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 360;
    video.crossOrigin = 'anonymous';
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const frameImage = canvas.toDataURL('image/jpeg', 0.92);
    setCapturedFrame({ url: frameImage, time: msTime });
  };

  const handleToggleMedia = () => {
    setLocalShowVideo((s) => !s);
  };

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleRewind = () => {
    if (videoRef.current) {
      videoRef.current.currentTime -= 10; // Rewind 10 seconds
    }
  };

  const handleDownload = () => {
    if (videoUrl) {
      const link = document.createElement('a');
      link.href = videoUrl;
      link.download = `location_${selectedMarker.id}_video.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleImageFullscreen = () => {
    if (!isFullscreen && imageRef.current) {
      if (imageRef.current.requestFullscreen) {
        imageRef.current.requestFullscreen();
      } else if (imageRef.current.webkitRequestFullscreen) {
        imageRef.current.webkitRequestFullscreen();
      } else if (imageRef.current.mozRequestFullScreen) {
        imageRef.current.mozRequestFullScreen();
      } else if (imageRef.current.msRequestFullscreen) {
        imageRef.current.msRequestFullscreen();
      }
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
    }
  };

  return (
    <StyledCard
      sx={{
        minHeight: 350,
        opacity: isAddSign && activeStep !== 0 && 0.6,
        filter: isAddSign && activeStep !== 0 && 'saturate(0.3) brightness(0.8)',
        pointerEvents: isAddSign && activeStep !== 0 && 'none',
        border: isAddSign && activeStep !== 0 && '1px dashed',
        transition: 'filter 0.3s ease',
        '&::before':
          isAddSign && activeStep !== 0
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `repeating-linear-gradient(
              45deg,
              rgba(200, 200, 200, 0.4),
              rgba(200, 200, 200, 0.4) 15px,
              rgba(220, 220, 220, 0.3) 15px,
              rgba(220, 220, 220, 0.3) 30px
            )`,
                backgroundSize: '42px 42px',
                pointerEvents: 'none',
                zIndex: 1,
              }
            : {},
        '&::after':
          isAddSign && activeStep !== 0
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-50%',
                width: '50%',
                height: '100%',
                background:
                  'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent)',
                pointerEvents: 'none',
                zIndex: 2,
              }
            : {},
      }}
    >
      {!isAddSign && (
        <CardHeader
          sx={{ '&.MuiCardHeader-root': { padding: '8px 16px' } }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography color="textSecondary" variant="h6">
                {localShowVideo ? 'Captured video' : 'Detected Frame'}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!localShowVideo}
                    onChange={handleToggleMedia}
                    color="primary.rieker"
                    disabled={!!showVideo}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {localShowVideo ? <Videocam fontSize="small" /> : <Image fontSize="small" />}
                    <Typography variant="body2">{localShowVideo ? 'Video' : 'Photo'}</Typography>
                  </Box>
                }
              />
            </Box>
          }
        />
      )}

      {loading ? (
        <Skeleton
          sx={{ height: '100%', margin: 3, borderRadius: 2 }}
          animation="wave"
          variant="rectangular"
        />
      ) : (
        <CardContent
          sx={{ '&.MuiCardContent-root': { padding: '8px 16px' }, marginTop: isAddSign && 2 }}
        >
          {localShowVideo ? (
            <Box sx={{ position: 'relative' }}>
              {videoUrl && (
                <>
                  <Box
                    component="video"
                    ref={videoRef}
                    src={videoUrl}
                    crossOrigin="anonymous"
                    controls
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      maxHeight: '350px',
                      backgroundColor: 'black',
                    }}
                  />

                  {showVideo && localShowVideo ? (
                    <VideoControls>
                      <Box
                        sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}
                      >
                        <Tooltip title="Pause video to Frame Capture">
                          <span>
                            <AddButton onClick={captureFrame}>Capture Frame</AddButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </VideoControls>
                  ) : (
                    <VideoControls>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Rewind 10s">
                          <IconButton onClick={handleRewind} size="small">
                            <Replay10 />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Play">
                          <IconButton onClick={handlePlay} size="small">
                            <PlayArrow />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Pause">
                          <IconButton onClick={handlePause} size="small">
                            <Pause />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Tooltip title="Download Video">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          onClick={handleDownload}
                          color="info"
                        >
                          Download
                        </Button>
                      </Tooltip>
                    </VideoControls>
                  )}

                  {capturedFrame.url && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <img
                        ref={capturedFrameRef}
                        crossOrigin="anonymous"
                        src={capturedFrame.url}
                        alt={`frame-${capturedFrame.time}`}
                        style={{ width: 120, height: 'auto', borderRadius: 6, objectFit: 'cover' }}
                      />
                      <Box>
                        <Typography variant="body2">Captured at: {capturedFrame.time}ms</Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <ImageContainer>
              <Box sx={{ position: 'relative' }}>
                {eventImageLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2,
                      minHeight: '350px',
                      backgroundColor: 'gray',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress size={100} sx={{ color: 'primary.rieker' }} />
                    <Typography variant="body1" sx={{ color: 'white' }}>
                      Loading image...
                    </Typography>
                  </Box>
                ) : eventImageError ? (
                  <Alert
                    severity="error"
                    sx={{
                      maxWidth: '500px',
                      bgcolor: '#c62828',
                      color: '#ffffff',
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Failed to load image
                    </Typography>
                    <Typography variant="body2">
                      {eventImageError ||
                        'An error occurred while loading the image. Please try again.'}
                    </Typography>
                  </Alert>
                ) : (
                  eventImage && (
                    <>
                      <img
                        ref={imageRef}
                        src={eventImage || logo}
                        alt={selectedMarker.name || 'Location Image'}
                        onError={(e) => {
                          if (e.currentTarget.src !== logo) {
                            e.currentTarget.src = logo;
                          }
                        }}
                        style={{
                          width: '100%',
                          borderRadius: 8,
                          maxHeight: '350px',
                          objectFit: 'contain',
                          cursor: 'pointer',
                        }}
                      />
                      <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                        <IconButton
                          onClick={handleImageFullscreen}
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(0,0,0,0.4)',
                            color: 'white',
                            zIndex: 2,
                            '&:hover': { background: 'rgba(0,0,0,0.7)' },
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M7 14H5v5h5v-2H7v-3zm0-4V7h3V5H5v5h2zm10 7h-3v2h5v-5h-2v3zm0-12v3h2V5h-5v2h3z"
                            />
                          </svg>
                        </IconButton>
                      </Tooltip>
                    </>
                  )
                )}
              </Box>
            </ImageContainer>
          )}
        </CardContent>
      )}
    </StyledCard>
  );
};

export default MediaComponent;

MediaComponent.propTypes = {
  selectedMarker: PropTypes.object.isRequired,
  driveVideoURL: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  setLoading: PropTypes.func.isRequired,
  showVideo: PropTypes.bool.isRequired,
  isAddSign: PropTypes.bool.isRequired,
  activeStep: PropTypes.number.isRequired,
  capturedFrame: PropTypes.object.isRequired,
  setCapturedFrame: PropTypes.func.isRequired,
};
